import React from "react";
import { Card, TextArea, IconButton, Icon, Badge, HStack, VStack } from "@astryxdesign/core";
import type { TextAreaStatus } from "@astryxdesign/core";
import { X } from "lucide-react";
import styles from "./QuestionCard.module.css";

export interface QuestionCardProps {
  /** Ref forwarded to the card's root element (used for scroll-to targeting). */
  ref?: React.Ref<HTMLDivElement>;
  /** 1-based position of this question within its section. */
  index: number;
  question: string;
  onQuestionChange: (value: string) => void;
  questionPlaceholder?: string;
  /** Validation status for the question field (error styling + message). */
  questionStatus?: TextAreaStatus;
  /** Forwarded as `data-field-key` on the question TextArea, for scroll-to-error targeting. */
  questionFieldKey?: string;
  idealResponse: string;
  onIdealResponseChange: (value: string) => void;
  idealResponsePlaceholder?: string;
  /** @default false */
  isDisabled?: boolean;
  /**
   * Plays a one-shot grow-in + fade entrance animation, for a card that was
   * just inserted via an `InsertZone`. The caller is responsible for
   * flipping this back to `false` after the animation completes (it does
   * not reset itself). No-ops under `prefers-reduced-motion: reduce`.
   * @default false
   */
  isEntering?: boolean;
  /**
   * When provided, renders a delete (X) control, hidden by default and
   * revealed on card hover or keyboard focus within the card.
   */
  onDelete?: () => void;
  deleteLabel?: string;
}

/**
 * Bounded card for a single question in the script-template editor: a
 * 1-based number Badge next to the question TextArea (no drag handle — the
 * NavRail is the only reorder surface), an always-visible ideal-response
 * field, and a hover-revealed delete control. The question and its
 * ideal-response field share the same inset column (offset by the number
 * Badge), so the ideal field lines up under the question rather than
 * spanning the full card.
 *
 * All spacing here is HStack/VStack `gap`, and the number renders via
 * Astryx's `Badge` — not hand-rolled CSS. This MUST be rendered inside an
 * Astryx <Theme> scope.
 */
const QuestionCard = ({
  ref,
  index,
  question,
  onQuestionChange,
  questionPlaceholder,
  questionStatus,
  questionFieldKey,
  idealResponse,
  onIdealResponseChange,
  idealResponsePlaceholder,
  isDisabled = false,
  isEntering = false,
  onDelete,
  deleteLabel,
}: QuestionCardProps): React.ReactElement => {
  // Disabled + never had an ideal response: nothing to show, so rendering
  // an empty read-only field would just be dead space. Skip it entirely.
  const hideIdealSection = isDisabled && !idealResponse;

  return (
    <div className={isEntering ? styles.enterWrapper : undefined}>
      <Card variant="default" className={styles.card} ref={ref}>
        <HStack align="start" gap={3}>
          <div className={styles.indexBadge}>
            <Badge variant="neutral" label={String(index)} />
          </div>
          <VStack gap={3} className={styles.body}>
            <TextArea
              label={`Question ${index}`}
              value={question}
              onChange={onQuestionChange}
              placeholder={questionPlaceholder ?? "Write the question you'll ask the candidate…"}
              rows={2}
              width="100%"
              isDisabled={isDisabled}
              data-field-key={questionFieldKey}
              status={questionStatus}
            />

            {!hideIdealSection && (
              <TextArea
                label="Ideal response"
                isOptional
                value={idealResponse}
                onChange={onIdealResponseChange}
                placeholder={
                  idealResponsePlaceholder ??
                  "Describe what a strong answer covers…"
                }
                rows={2}
                size="sm"
                width="100%"
                isDisabled={isDisabled}
              />
            )}
          </VStack>
          {onDelete && !isDisabled && (
            <div className={styles.delete}>
              <IconButton
                icon={<Icon icon={X} color="secondary" />}
                label={deleteLabel ?? "Delete question"}
                variant="ghost"
                onClick={onDelete}
              />
            </div>
          )}
        </HStack>
      </Card>
    </div>
  );
};

export default QuestionCard;
